import { BaseRHFWidgetProps } from "@impero-com/common/components/form/rhfFields/types";
import { FieldValues, useController } from "react-hook-form";
import { ZodNullable, ZodNumber, ZodString } from "zod";

import {
    RadioButtonGroupInput,
    RadioButtonGroupInputBaseProps,
    RadioButtonGroupInputValueType,
} from "../fields/radioButtonGroupInput";

export interface RHFRadioButtonGroupInputProps<
    TFieldsValues extends FieldValues,
    T,
>
    extends
        RadioButtonGroupInputBaseProps<T>,
        BaseRHFWidgetProps<TFieldsValues, RadioButtonGroupInputValueType> {
    schema?: ZodString | ZodNumber | ZodNullable<ZodString | ZodNumber>;
}

/**
 * Basic form component based on ant design's Radio.Group
 *
 * This component leverages the following constraints from the validator:
 *  - `.nullable()` is mapped to allowDeselect
 */
export const RHFRadioButtonGroupInput = <
    TFieldValues extends FieldValues,
    T extends RadioButtonGroupInputValueType,
>({
    name,
    control,
    label,
    options,
    disabled,
    columns,
    schema,
    direction,
    allowDeselect,
    type,
}: RHFRadioButtonGroupInputProps<TFieldValues, T>) => {
    const { field, fieldState } = useController({ name, control, disabled });

    // A schema can disallow null, but we may still want to allow the user to deselect
    const allowDeselectSchemaFallback = allowDeselect ?? schema?.isNullable();

    return (
        <RadioButtonGroupInput
            value={field.value}
            error={fieldState.error?.message}
            onChange={field.onChange}
            onBlur={field.onBlur}
            fieldRef={field.ref}
            label={label}
            options={options}
            disabled={field.disabled}
            columns={columns}
            direction={direction}
            allowDeselect={allowDeselectSchemaFallback}
            type={type}
        />
    );
};
